import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TrendingUp, BarChart3, CalendarDays, Package, Filter, RefreshCw, DollarSign, ShoppingCart, Users2, Boxes, Download, History } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const N = (n) => Number(n || 0).toLocaleString('en-ZA');

const CHART_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

export default function SalesAnalyticsPage() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(today);
  const [tradingOnly, setTradingOnly] = useState(false);
  const [category, setCategory] = useState('ALL');
  const [paymentMethod, setPaymentMethod] = useState('ALL');
  const [chartMode, setChartMode] = useState('week'); // 'week' | 'day' | 'month' | '3mweek'

  const [data, setData] = useState(null);
  const [byMonthData, setByMonthData] = useState([]);
  const [multiWeekData, setMultiWeekData] = useState([]);
  const [allTimeTop, setAllTimeTop] = useState([]);
  const [topSortBy, setTopSortBy] = useState('revenue'); // per-month Top Products sort
  const [alltimeSortBy, setAlltimeSortBy] = useState('revenue'); // top sellers sort
  const [alltimePeriod, setAlltimePeriod] = useState(0); // 0 = all-time, 3, 6, 12
  const [alltimePeriodLabel, setAlltimePeriodLabel] = useState('All-time');
  const [filters, setFilters] = useState({ categories: [], payment_methods: [], months: [] });
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [applyingCodes, setApplyingCodes] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month, trading_days_only: tradingOnly };
      if (category !== 'ALL') params.category = category;
      if (paymentMethod !== 'ALL') params.payment_method = paymentMethod;
      const compareParams = {};
      if (category !== 'ALL') compareParams.category = category;
      if (paymentMethod !== 'ALL') compareParams.payment_method = paymentMethod;

      const [a, f, bm, mw, top] = await Promise.all([
        axios.get(`${API}/sales-analytics`, { headers, params }),
        axios.get(`${API}/sales-analytics/filters`, { headers }).catch(() => ({ data: filters })),
        axios.get(`${API}/sales-analytics/by-month`, { headers, params: { months: 12, ...compareParams } }),
        axios.get(`${API}/sales-analytics/multi-week`, { headers, params: { months: 3, ...compareParams } }),
        axios.get(`${API}/sales-analytics/top-sellers-alltime`, { headers, params: { limit: 10, sort_by: alltimeSortBy, period_months: alltimePeriod } }),
      ]);
      setData(a.data);
      setFilters(f.data);
      setByMonthData(bm.data?.rows || []);
      setMultiWeekData(mw.data?.rows || []);
      setAllTimeTop(top.data?.rows || []);
      setAlltimePeriodLabel(top.data?.period_label || 'All-time');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [month, tradingOnly, category, paymentMethod, alltimeSortBy, alltimePeriod]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Apply Master Product Codes ----------------------------------------
  const applyMasterCodes = useCallback(async () => {
    try {
      const pv = await axios.get(`${API}/master-codes/preview`, { headers });
      const p = pv.data;
      const msg = `MASTER PRODUCT CODES (${p.master_count} codes)\n\n` +
        `Parts Catalog: ${p.parts_catalog.updated} updated · ${p.parts_catalog.inserted} new · ${p.parts_catalog.skipped_no_match} kept as-is\n` +
        `Spares Products: ${p.auto_spares_products.updated} updated · ${p.auto_spares_products.inserted} new · ${p.auto_spares_products.skipped_no_match} kept as-is\n` +
        `Existing Sales: ${p.auto_spares_sales.relinked} re-linked · ${p.auto_spares_sales.skipped_no_match} kept as-is\n\n` +
        `Existing prices and quantities are PRESERVED. No data is deleted. Continue?`;
      if (!window.confirm(msg)) return;
      setApplyingCodes(true);
      const res = await axios.post(`${API}/master-codes/apply`, {}, { headers });
      const r = res.data;
      toast.success(`Catalog: ${r.parts_catalog.updated + r.auto_spares_products.updated} updated, ${r.parts_catalog.inserted + r.auto_spares_products.inserted} new. Sales re-linked: ${r.auto_spares_sales.relinked}.`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply master codes');
    } finally { setApplyingCodes(false); }
  }, [headers, fetchData]);

  // ---- Restore March 2026 backfill ----------------------------------------
  const restoreMarch = useCallback(async () => {
    try {
      const pv = await axios.get(`${API}/sales-analytics/restore-march-2026/preview`, { headers });
      const p = pv.data;
      const msg = `This will insert ${p.sales.will_insert} sales (R ${Number(p.sales.revenue_total).toLocaleString('en-ZA')}) and ${p.expenses.will_insert} expenses (R ${Number(p.expenses.amount_total).toLocaleString('en-ZA')}) for March 2026.\n\n` +
        `${p.sales.already_present} sales + ${p.expenses.already_present} expenses are already present and will be skipped.\n\nContinue?`;
      if (!window.confirm(msg)) return;
      setRestoring(true);
      const res = await axios.post(`${API}/sales-analytics/restore-march-2026`, {}, { headers });
      toast.success(res.data?.message || 'March 2026 data restored');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore March 2026 data');
    } finally { setRestoring(false); }
  }, [headers, fetchData]);

  // ---- CSV export of currently-selected month -----------------------------
  const downloadCsv = useCallback(() => {
    if (!data) { toast.error('No data to export'); return; }
    const s = data.summary || {};
    const rows = [];
    rows.push(['Spares Sales Analytics — ', month]);
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Revenue', s.revenue ?? 0]);
    rows.push(['Units sold', s.units ?? 0]);
    rows.push(['Sales count', s.sales_count ?? 0]);
    rows.push(['Avg sale', s.avg_sale ?? 0]);
    rows.push([]);
    rows.push(['By Day', 'Day', 'Revenue', 'Units', 'Sales']);
    (data.by_day || []).forEach(d => rows.push(['', d.day, d.revenue, d.units, d.sales_count]));
    rows.push([]);
    rows.push(['By Week', 'Week', 'Revenue', 'Units', 'Sales']);
    (data.by_week || []).forEach(w => rows.push(['', w.short_label || w.label, w.revenue, w.units, w.sales_count]));
    rows.push([]);
    rows.push(['Top Products', 'Description', 'Units', 'Revenue']);
    (data.top_products || []).forEach(p => rows.push(['', p.description, p.units, p.revenue]));
    rows.push([]);
    rows.push(['Top Salesmen', 'Salesman', 'Revenue', 'Sales']);
    (data.top_salesmen || []).forEach(sm => rows.push(['', sm.salesman_name, sm.revenue, sm.sales_count]));

    const csv = rows.map(r => r.map(c => {
      const v = c === null || c === undefined ? '' : String(c);
      return v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sales-analytics-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data, month]);


  // Chart data
  const chartData = useMemo(() => {
    if (chartMode === 'month') {
      return byMonthData.map(m => ({ label: m.short_label, revenue: m.revenue, units: m.units, sales: m.sales_count }));
    }
    if (chartMode === '3mweek') {
      return multiWeekData.map(w => ({ label: w.short_label, revenue: w.revenue, units: w.units, sales: w.sales_count, month: w.month }));
    }
    if (!data) return [];
    if (chartMode === 'week') {
      return data.by_week.map(w => ({ label: w.short_label, revenue: w.revenue, units: w.units, sales: w.sales_count }));
    }
    return data.by_day.map(d => ({
      label: String(d.day).padStart(2, '0'),
      revenue: d.revenue,
      units: d.units,
      sales: d.sales_count,
      isWeekend: d.is_weekend,
    }));
  }, [data, byMonthData, multiWeekData, chartMode]);

  // Color cells by month-group when in 3-month-weekly mode so the user can
  // visually see where one month ends and the next starts.
  const monthGroupColors = useMemo(() => {
    if (chartMode !== '3mweek') return null;
    const groups = {};
    let idx = 0;
    multiWeekData.forEach(w => {
      if (groups[w.month] === undefined) { groups[w.month] = idx++; }
    });
    return groups;
  }, [chartMode, multiWeekData]);

  const peakDay = useMemo(() => {
    if (!data?.by_day?.length) return null;
    return [...data.by_day].sort((a, b) => b.revenue - a.revenue)[0];
  }, [data]);

  const summary = data?.summary || {};

  return (
    <DashboardLayout>
      <div className="space-y-4 p-2 sm:p-4">
        {/* Header card */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-slate-900">
                <BarChart3 className="h-6 w-6 text-blue-600" /> Spares Sales Analytics
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Product-level breakdown of <b>auto_spares_sales</b> for the chosen month. Toggle weekly vs daily view, filter by category &amp; payment, and exclude weekends if you only trade Mon-Fri.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={downloadCsv} variant="outline" disabled={loading || !data} data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
              <Button onClick={applyMasterCodes} variant="outline" disabled={applyingCodes} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="apply-master-codes-btn">
                <Boxes className={`h-4 w-4 mr-1 ${applyingCodes ? 'animate-spin' : ''}`} /> {applyingCodes ? 'Applying…' : 'Apply Master Codes'}
              </Button>
              <Button onClick={restoreMarch} variant="outline" disabled={restoring} className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="restore-march-2026-btn">
                <History className={`h-4 w-4 mr-1 ${restoring ? 'animate-spin' : ''}`} /> {restoring ? 'Restoring…' : 'Restore March 2026'}
              </Button>
              <Button onClick={fetchData} variant="outline" disabled={loading} data-testid="refresh-analytics-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter row */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Month</label>
                <Input
                  type="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  className="w-44"
                  data-testid="analytics-month"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-44" data-testid="analytics-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All categories</SelectItem>
                    {filters.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Payment</label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="w-36" data-testid="analytics-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All methods</SelectItem>
                    {filters.payment_methods.map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Filter className="h-4 w-4 text-slate-500" />
                <label className="flex items-center gap-2 text-sm select-none cursor-pointer" data-testid="trading-days-toggle">
                  <input
                    type="checkbox"
                    checked={tradingOnly}
                    onChange={(e) => setTradingOnly(e.target.checked)}
                    className="h-4 w-4 accent-blue-600"
                  />
                  Trading days only (Mon-Fri)
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <SummaryTile icon={DollarSign} label="Revenue" value={R(summary.total_revenue)} color="bg-emerald-50 text-emerald-700 border-emerald-200" testid="tile-revenue" />
          <SummaryTile icon={TrendingUp} label="Gross Profit" value={R(summary.gross_profit)} sub={`${summary.margin_pct || 0}% margin`} color="bg-blue-50 text-blue-700 border-blue-200" testid="tile-gross-profit" />
          <SummaryTile icon={ShoppingCart} label="Sales Count" value={N(summary.sales_count)} sub={`avg ${R(summary.avg_basket)}`} color="bg-violet-50 text-violet-700 border-violet-200" testid="tile-sales-count" />
          <SummaryTile icon={Boxes} label="Units Sold" value={N(summary.total_units)} color="bg-amber-50 text-amber-700 border-amber-200" testid="tile-units" />
          <SummaryTile
            icon={CalendarDays}
            label={tradingOnly ? 'Trading days w/ sales' : 'Days w/ sales'}
            value={`${summary.days_with_sales || 0} / ${tradingOnly ? summary.trading_days_in_period : (data?.by_day?.length || 0)}`}
            sub={peakDay ? `Peak: ${peakDay.date} (${R(peakDay.revenue)})` : ''}
            color="bg-rose-50 text-rose-700 border-rose-200"
            testid="tile-active-days"
          />
        </div>

        {/* Chart */}
        <Card className="border-slate-200">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              {chartMode === 'week'   && `Revenue by Week — ${month}`}
              {chartMode === 'day'    && `Revenue by Day — ${month}${tradingOnly ? ' (weekdays)' : ''}`}
              {chartMode === 'month'  && 'Revenue by Month (last 12 months)'}
              {chartMode === '3mweek' && 'Weekly Revenue — last 3 months'}
            </CardTitle>
            <div className="inline-flex flex-wrap rounded-md border border-slate-300 overflow-hidden" data-testid="chart-mode-toggle">
              <button
                onClick={() => setChartMode('week')}
                className={`px-3 py-1 text-xs ${chartMode === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="chart-mode-week"
              >By Week</button>
              <button
                onClick={() => setChartMode('day')}
                className={`px-3 py-1 text-xs border-l border-slate-300 ${chartMode === 'day' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="chart-mode-day"
              >By Day</button>
              <button
                onClick={() => setChartMode('month')}
                className={`px-3 py-1 text-xs border-l border-slate-300 ${chartMode === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="chart-mode-month"
              >By Month</button>
              <button
                onClick={() => setChartMode('3mweek')}
                className={`px-3 py-1 text-xs border-l border-slate-300 ${chartMode === '3mweek' ? 'bg-blue-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="chart-mode-3mweek"
              >3-Month Weekly</button>
            </div>
          </CardHeader>
          <CardContent>
            {chartData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-12" data-testid="chart-empty">No sales data for the selected period.</p>
            ) : (
              <div style={{ width: '100%', height: 320 }} data-testid="sales-chart">
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 12, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value, name) => name === 'revenue' ? [R(value), 'Revenue'] : [N(value), name === 'units' ? 'Units' : 'Sales']}
                      labelFormatter={(l) => chartMode === 'day' ? `Day ${l}` : l}
                      contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    />
                    <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.isWeekend ? '#94a3b8' : CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tables: top products + salesmen + categories */}
        <div className="grid lg:grid-cols-3 gap-3">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
                  <Package className="h-4 w-4" /> Top Products
                </CardTitle>
                <SortToggle value={topSortBy} onChange={setTopSortBy} testid="top-products-sort" />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {(data?.top_products || []).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">No product sales in this period.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="top-products">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Product</TableHead>
                        <TableHead className="text-xs text-right">Units</TableHead>
                        <TableHead className="text-xs text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...(data?.top_products || [])]
                        .sort((a, b) => (topSortBy === 'units' ? b.units - a.units : b.revenue - a.revenue))
                        .slice(0, 10)
                        .map((r, i) => (
                          <TableRow key={i}>
                            <TableCell className="py-2 text-sm font-medium">{r.part_description}</TableCell>
                            <TableCell className={`py-2 text-sm text-right ${topSortBy === 'units' ? 'font-bold text-emerald-700' : ''}`}>{N(r.units)}</TableCell>
                            <TableCell className={`py-2 text-sm text-right ${topSortBy === 'revenue' ? 'font-semibold text-emerald-700' : ''}`}>{R(r.revenue)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          <TopTable
            title="Top Salesmen"
            icon={Users2}
            color="text-blue-700"
            rows={(data?.top_salesmen || []).slice(0, 10)}
            columns={[
              { key: 'salesman_name', label: 'Salesman', render: r => <span className="font-medium text-sm">{r.salesman_name}</span> },
              { key: 'sales_count', label: 'Sales', align: 'right', render: r => N(r.sales_count) },
              { key: 'revenue', label: 'Revenue', align: 'right', render: r => <span className="text-blue-700 font-semibold">{R(r.revenue)}</span> },
            ]}
            emptyMsg="No salesman activity."
            testid="top-salesmen"
          />
          <TopTable
            title="By Category"
            icon={Boxes}
            color="text-amber-700"
            rows={data?.by_category || []}
            columns={[
              { key: 'category', label: 'Category', render: r => <span className="font-medium text-sm">{r.category}</span> },
              { key: 'units', label: 'Units', align: 'right', render: r => N(r.units) },
              { key: 'revenue', label: 'Revenue', align: 'right', render: r => <span className="text-amber-700 font-semibold">{R(r.revenue)}</span> },
            ]}
            emptyMsg="No categorised sales."
            testid="by-category"
          />
        </div>

        {/* ===== TOP 10 BEST SELLERS (window selectable) ===== */}
        <Card className="border-slate-200" data-testid="alltime-top-card">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2 text-rose-700">
                  <TrendingUp className="h-4 w-4" /> Top 10 Best Sellers — {alltimePeriodLabel}
                </CardTitle>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Ignores the month/category/payment filters above — period &amp; sort below set the ranking.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-[11px]" data-testid="alltime-period">
                  {[
                    { v: 0,  label: 'All time' },
                    { v: 3,  label: '3 months' },
                    { v: 6,  label: '6 months' },
                    { v: 12, label: '12 months' },
                  ].map(({ v, label }) => (
                    <button
                      key={v}
                      onClick={() => setAlltimePeriod(v)}
                      className={`px-2 py-0.5 border-l border-slate-300 first:border-l-0 ${alltimePeriod === v ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                      data-testid={`alltime-period-${v}`}
                    >{label}</button>
                  ))}
                </div>
                <SortToggle value={alltimeSortBy} onChange={setAlltimeSortBy} testid="alltime-sort" />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {allTimeTop.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">No sales captured yet.</p>
            ) : (
              <div className="overflow-x-auto" data-testid="alltime-top-table">
                <Table>
                  <TableHeader className="bg-rose-50">
                    <TableRow>
                      <TableHead className="text-xs w-10">#</TableHead>
                      <TableHead className="text-xs">Product</TableHead>
                      <TableHead className="text-xs">Category</TableHead>
                      <TableHead className="text-xs text-right">Units</TableHead>
                      <TableHead className="text-xs text-right">Sales</TableHead>
                      <TableHead className="text-xs text-right">Revenue</TableHead>
                      <TableHead className="text-xs text-right">Gross Profit</TableHead>
                      <TableHead className="text-xs">First → Last</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allTimeTop.map((r, i) => (
                      <TableRow key={i} data-testid={`alltime-row-${i}`}>
                        <TableCell className="py-2 text-sm font-bold text-rose-700">{i + 1}</TableCell>
                        <TableCell className="py-2 text-sm font-medium">{r.part_description}</TableCell>
                        <TableCell className="py-2 text-xs text-slate-600">{r.category || '—'}</TableCell>
                        <TableCell className={`py-2 text-sm text-right ${alltimeSortBy === 'units' ? 'font-bold text-rose-700' : ''}`}>{N(r.units)}</TableCell>
                        <TableCell className="py-2 text-sm text-right">{N(r.sales_count)}</TableCell>
                        <TableCell className={`py-2 text-sm text-right ${alltimeSortBy === 'revenue' ? 'font-semibold text-emerald-700' : ''}`}>{R(r.revenue)}</TableCell>
                        <TableCell className="py-2 text-sm text-right text-blue-700">{R(r.gross_profit)}<span className="text-[10px] text-slate-500 ml-1">({r.margin_pct}%)</span></TableCell>
                        <TableCell className="py-2 text-[11px] text-slate-600 whitespace-nowrap">{r.first_sold} → {r.last_sold}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function SummaryTile({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className={`rounded-lg border p-3 ${color}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-80">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <p className="text-xl font-bold mt-1 leading-tight">{value}</p>
      {sub && <p className="text-[11px] opacity-75 mt-0.5">{sub}</p>}
    </div>
  );
}

function SortToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md border border-slate-300 overflow-hidden text-[11px]" data-testid={testid}>
      <button
        onClick={() => onChange('revenue')}
        className={`px-2 py-0.5 ${value === 'revenue' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
        data-testid={`${testid}-revenue`}
      >By Revenue</button>
      <button
        onClick={() => onChange('units')}
        className={`px-2 py-0.5 border-l border-slate-300 ${value === 'units' ? 'bg-rose-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
        data-testid={`${testid}-units`}
      >By Qty</button>
    </div>
  );
}

function TopTable({ title, icon: Icon, color, rows, columns, emptyMsg, testid }) {
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <CardTitle className={`text-sm flex items-center gap-2 ${color}`}>
          <Icon className="h-4 w-4" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">{emptyMsg}</p>
        ) : (
          <div className="overflow-x-auto" data-testid={testid}>
            <Table>
              <TableHeader>
                <TableRow>
                  {columns.map(c => (
                    <TableHead key={c.key} className={`text-xs ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i}>
                    {columns.map(c => (
                      <TableCell key={c.key} className={`py-2 text-sm ${c.align === 'right' ? 'text-right' : ''}`}>
                        {c.render(r)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
